import axiosInstance from '@/lib/axios/config/axiosConfig';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { CallQueueType } from '@/constants/queue';

// User role enum
// eslint-disable-next-line react-refresh/only-export-components
export enum UserRole {
  ADMIN = 'Admin',
  MANAGER = 'Manager'
}

// Data interfaces
export interface Queue {
  label: string;
  microsoftId: string;
  type: string;
  agentMicrosoftIds: string[];
}

export interface Agent {
  name: string;
  microsoftId: string;
  queueId?: string,
  queueType: CallQueueType
}

export interface QueueResponse {
  id: number;
  microsoftId: string;
  name: string;
  agents: Agent[];
  type: string;
}

export interface QueueWithRoleResponse {
  userRole: UserRole;
  queues: QueueResponse[];
}

// Context interface
interface AppDataContextType {
  // Queue data
  queues: Queue[];
  directAgentGroups: Queue[];
  agents: Agent[];
  queuesResponse: QueueResponse[];
  directAgentGroupIds: number[];
  queueMicrosoftIds: string[];
  uniqueAgents: Agent[];
  userRole: UserRole | null;

  // Loading states
  loaded: boolean;

  // Error states
  errors: {
    queues: string | null;
    agents: string | null;
  };

  // Refresh methods
  fetchQueuesWithRole: () => Promise<void>;
  removeAgentByMicrosoftId: (microsoftUserId: string) => void;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [queuesResponse, setQueuesResponse] = useState<QueueResponse[]>([]);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const { gromToken } = useAuth();

  // Queue and Agent states - removed individual states, will use useMemo instead

  const directAgentGroupIds = useMemo(() => {
    return queuesResponse.filter(queue => queue.type === CallQueueType.DIRECT_AGENT_GROUP).map(queue => queue.id) || [];
  }, [queuesResponse]);

  const queueMicrosoftIds = useMemo(() => {
    return queuesResponse.filter(queue => queue.type === CallQueueType.CALL_QUEUE).map(queue => queue.microsoftId) || [];
  }, [queuesResponse]);

  const getQueueId = (queue: QueueResponse) => {
    return queue.type === CallQueueType.CALL_QUEUE ? queue.microsoftId : queue.id?.toString();
  }

  // Computed values from queuesResponse - no setState needed, instant access
  const queues = useMemo(() => {
    const mappedQueues = queuesResponse.map((queue) => ({
      label: queue.name,
      microsoftId: getQueueId(queue),
      type: queue.type,
      agentMicrosoftIds: queue.agents.map(agent => agent.microsoftId)
    }));
    return mappedQueues.filter(queue => queue.type === CallQueueType.CALL_QUEUE);
  }, [queuesResponse]);

  const directAgentGroups = useMemo(() => {
    const mappedQueues = queuesResponse.map((queue) => ({
      label: queue.name,
      microsoftId: getQueueId(queue),
      type: queue.type,
      agentMicrosoftIds: queue.agents.map(agent => agent.microsoftId)
    }));
    return mappedQueues.filter(queue => queue.type === CallQueueType.DIRECT_AGENT_GROUP);
  }, [queuesResponse]);

  const agents = useMemo(() => {
    return queuesResponse.flatMap((queue) =>
      queue.agents?.map((agent) => ({
        name: agent.name,
        microsoftId: agent.microsoftId,
        queueType: queue.type as CallQueueType,
        queueId: getQueueId(queue)
      })) || []
    );
  }, [queuesResponse]);

  const uniqueAgents = useMemo(() => {
    return Array.from(
      new Map(agents.map((agent) => [agent.microsoftId, agent])).values()
    );
  }, [agents]);


  // Loading states
  const [loaded, setLoaded] = useState(false);

  // Error states
  const [errors, setErrors] = useState({
    queues: null as string | null,
    agents: null as string | null
  });

  // Fetch queues with agents and user role
  const fetchQueuesWithRole = async () => {
    try {
      setLoaded(false);
      const response = await axiosInstance.get<QueueWithRoleResponse>(`/queues/with-agents-and-role`);

      setQueuesResponse(response.data.queues || []);
      setUserRole(response.data.userRole);
      setErrors(prev => ({ ...prev, queues: null }));
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        queues: 'Failed to fetch queue data with role'
      }));
      console.error('Error fetching queues with role:', err);
    } finally {
      setLoaded(true);
    }
  };

  // Remove agent from all queues by Microsoft User ID
  const removeAgentByMicrosoftId = (microsoftUserId: string) => {
    console.log('AppDataContext: Removing agent with microsoftUserId:', microsoftUserId);

    setQueuesResponse(prevQueues => {
      return prevQueues.map(queue => ({
        ...queue,
        agents: queue.agents.filter(agent => agent.microsoftId !== microsoftUserId)
      }));
    });
  };

  // Initial data fetch
  useEffect(() => {
    if (gromToken?.length > 0) {
      fetchQueuesWithRole();
    }
  }, [gromToken]);

  return (
    <AppDataContext.Provider
      value={{
        queuesResponse,
        uniqueAgents,
        queues,
        directAgentGroups,
        directAgentGroupIds,
        queueMicrosoftIds,
        agents,
        userRole,
        loaded,
        errors,
        fetchQueuesWithRole,
        removeAgentByMicrosoftId,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
}

export const WelcomeMessage = "Welcome to TCX Insight Reporting";
